import React, { useState, useEffect } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import '../styles/BlogSearch.css';

const BlogSearch = ({ posts = [], onSearchResults }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [searchResults, setSearchResults] = useState(posts);
  const [showFilters, setShowFilters] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const tags = [...new Set(posts.flatMap((post) => post.tags || []))];
    setAllTags(tags);
  }, [posts]);

  useEffect(() => {
    let filteredPosts = posts;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredPosts = filteredPosts.filter((post) => {
        return (
          post.title.toLowerCase().includes(searchLower) ||
          (post.excerpt || '').toLowerCase().includes(searchLower) ||
          (post.content || '').toLowerCase().includes(searchLower) ||
          (post.author || '').toLowerCase().includes(searchLower) ||
          (post.category || '').toLowerCase().includes(searchLower) ||
          (post.tags || []).some((tag) => tag.toLowerCase().includes(searchLower))
        );
      });
    }

    if (selectedTags.length > 0) {
      filteredPosts = filteredPosts.filter((post) =>
        selectedTags.some((tag) => (post.tags || []).includes(tag))
      );
    }

    setSearchResults(filteredPosts);

    const timeoutId = setTimeout(() => {
      onSearchResults(filteredPosts);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedTags, onSearchResults, posts]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      setIsRefreshing(true);
      // Trigger a brief refresh animation
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500);
    }
  };

  const handleTagClick = (tag) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTags([]);
    setShowFilters(false);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const hasActiveFilters = searchTerm.trim() || selectedTags.length > 0;

  // Close filters panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilters && !event.target.closest('.search-bar-container')) {
        setShowFilters(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilters]);

  // Auto-close filters panel when there's a search term
  useEffect(() => {
    if (searchTerm.trim() && showFilters) {
      setShowFilters(false);
    }
  }, [searchTerm, showFilters]);

  return (
    <div className="blog-search-compact">
      <div className="search-bar-container">
        <div className="search-bar">
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onClick={(e) => e.stopPropagation()}
              className={`search-input ${isRefreshing ? 'refreshing' : ''}`}
            />
            <div className="search-icon" aria-hidden="true">
              <Search size={18} strokeWidth={1.75} />
            </div>
          </div>
          
          <button 
            onClick={toggleFilters}
            className={`filter-toggle ${showFilters ? 'active' : ''}`}
            title="Toggle filters"
          >
            <span className="filter-icon" aria-hidden="true">
              <ChevronDown size={18} strokeWidth={1.75} />
            </span>
          </button>
        </div>

        {/* Expandable Filters */}
        {showFilters && !searchTerm.trim() && (
          <div className="filters-panel">
            <div className="filters-content">
              <div className="tag-filters">
                <h4>Filter by Topic:</h4>
                <div className="tag-list">
                  {allTags.map(tag => (
                    <button
                      key={tag}
                      onClick={() => handleTagClick(tag)}
                      className={`tag-filter ${selectedTags.includes(tag) ? 'active' : ''}`}
                    >
                      {tag}
                      {selectedTags.includes(tag) && (
                        <span className="tag-check" aria-hidden="true">
                          <Check size={14} strokeWidth={2} />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Active Filters & Results */}
              <div className="search-results-summary">
                <div className="results-info">
                  <span className="results-count">
                    {searchResults.length} article{searchResults.length !== 1 ? 's' : ''} found
                  </span>
                  {hasActiveFilters && (
                    <button onClick={clearFilters} className="clear-filters">
                      Clear all
                    </button>
                  )}
                </div>
                
                {/* Active Filters Display */}
                {hasActiveFilters && (
                  <div className="active-filters">
                    {searchTerm.trim() && (
                      <div className="active-filter">
                        <span>Search: "{searchTerm}"</span>
                        <button 
                          onClick={() => setSearchTerm('')}
                          className="remove-filter"
                          aria-label="Remove search filter"
                        >
                          <X size={14} strokeWidth={2} />
                        </button>
                      </div>
                    )}
                    {selectedTags.map(tag => (
                      <div key={tag} className="active-filter">
                        <span>Tag: {tag}</span>
                        <button 
                          onClick={() => handleTagClick(tag)}
                          className="remove-filter"
                          aria-label={`Remove ${tag} filter`}
                        >
                          <X size={14} strokeWidth={2} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogSearch;
